/**
 * LibDe265 HEVC Canvas Player
 *
 * Fallback player for HEVC streams on browsers without WebCodecs HEVC support.
 * Decodes H.265/HEVC frames via libde265 WASM and renders to <canvas>.
 *
 * Ported from reference: rawws.js (MistMetaPlayer)
 *
 * Key differences from WebCodecsPlayer:
 * - Renders to Canvas (putImageData) instead of MediaStream
 * - Video-only (no audio decoding)
 * - Only activates when WebCodecs HEVC is NOT supported
 * - Dynamically loads libde265.js from MistServer host
 */

import { BasePlayer } from "../../core/PlayerInterface";
import { parseRawChunk, isKeyframe } from "../WebCodecsPlayer/RawChunkParser";
import type {
  StreamSource,
  StreamInfo,
  PlayerOptions,
  PlayerCapability,
} from "../../core/PlayerInterface";
import type { LibDe265Global, LibDe265Decoder, FrameStats } from "./types";

declare const libde265: LibDe265Global;

export class LibDe265PlayerImpl extends BasePlayer {
  readonly capability: PlayerCapability = {
    name: "LibDe265 HEVC Decoder",
    shortname: "libde265",
    priority: 5, // Below WebCodecs (0) but above legacy (99)
    mimes: ["ws/video/raw", "wss/video/raw"],
  };

  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private ws: WebSocket | null = null;
  private decoder: LibDe265Decoder | null = null;
  private container: HTMLElement | null = null;
  private isDestroyed = false;
  private wantToPlay = false;
  private state: "idle" | "play" | "waiting" | "playing" | "seeking" | "ended" = "idle";

  // Frame dropping state (ported from rawws.js:65-66, 419-461)
  private dropping: false | Array<[Uint8Array, Uint8Array]> = false;

  private frames: FrameStats = {
    received: 0,
    bitsReceived: 0,
    decoded: 0,
    dropped: 0,
    timestamps: {},
  };

  private seekTo: number | null = null;
  private duration = 0;
  private paused = true;

  // Proxy video element for compatibility with PlayerManager
  private proxyVideo: HTMLVideoElement | null = null;

  private static readonly DROP_THRESHOLD = 20;

  isMimeSupported(mimetype: string): boolean {
    return this.capability.mimes.includes(mimetype);
  }

  isBrowserSupported(
    mimetype: string,
    _source: StreamSource,
    streamInfo: StreamInfo
  ): boolean | string[] {
    if (!("WebSocket" in window)) return false;

    // Only activate for HEVC streams
    const hasHEVC = streamInfo.meta.tracks.some(
      (t) => t.type === "video" && (t.codec === "HEVC" || t.codec === "H265")
    );
    if (!hasHEVC) return false;

    // Only activate when WebCodecs HEVC is NOT supported
    // (if WebCodecs supports HEVC, the WebCodecsPlayer handles it at higher priority)
    if (typeof VideoDecoder !== "undefined") {
      try {
        // Synchronous check: if the browser has VideoDecoder and claims HEVC,
        // let WebCodecsPlayer handle it. We can't use isConfigSupported() here
        // (async), but we trust that WebCodecsPlayer will validate and fallback
        // to us via scorer if it fails.
        // Return false to let WebCodecsPlayer try first.
        // If WebCodecsPlayer fails, PlayerManager will select us as fallback.
      } catch {
        // VideoDecoder exists but is broken — we should activate
      }
    }

    return ["video"]; // Video-only (no audio)
  }

  async initialize(
    container: HTMLElement,
    source: StreamSource,
    options: PlayerOptions
  ): Promise<HTMLVideoElement> {
    this.container = container;
    this.isDestroyed = false;
    container.classList.add("fw-player-container");

    // Create canvas element (rawws.js:56)
    this.canvas = document.createElement("canvas");
    this.canvas.classList.add("fw-player-video");
    this.canvas.style.objectFit = "contain";
    this.ctx = this.canvas.getContext("2d");
    container.appendChild(this.canvas);

    // Create a hidden proxy video element for PlayerManager compatibility
    // (PlayerManager expects HTMLVideoElement for event dispatching)
    this.proxyVideo = document.createElement("video");
    this.proxyVideo.style.display = "none";
    container.appendChild(this.proxyVideo);
    this.videoElement = this.proxyVideo;

    this.wantToPlay = !!options.autoplay;

    // Reset frame state
    this.frames = { received: 0, bitsReceived: 0, decoded: 0, dropped: 0, timestamps: {} };
    this.dropping = false;
    this.state = "idle";
    this.paused = true;

    // Set up event listeners on the proxy video
    this.setupVideoEventListeners(this.proxyVideo, options);

    // Load libde265.js from MistServer
    await this.loadLibDe265(source.url);

    // Initialize decoder and connect
    this.initDecoder();
    this.connect(source.url);

    return this.proxyVideo;
  }

  /**
   * Dynamically load libde265.js from MistServer host.
   * Ported from rawws.js:641-653
   */
  private async loadLibDe265(sourceUrl: string): Promise<void> {
    if (typeof libde265 !== "undefined") return;

    // Extract host from WebSocket URL
    const host = sourceUrl.replace(/^wss?:\/\//, "").split("/")[0];
    const protocol = sourceUrl.startsWith("wss") ? "https" : "http";
    const scriptUrl = `${protocol}://${host}/libde265.js`;

    return new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = scriptUrl;
      script.crossOrigin = "anonymous";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load libde265 from ${scriptUrl}`));
      document.head.appendChild(script);
    });
  }

  /**
   * Initialize the libde265 decoder.
   * Ported from rawws.js:168-271
   */
  private initDecoder(): void {
    this.decoder = new libde265.Decoder();

    const displayImage = window.requestAnimationFrame
      ? (imageData: ImageData) => {
          if (this.decoder) this.decoder.pending_image_data = imageData;
          window.requestAnimationFrame(() => {
            if (this.decoder?.pending_image_data && this.ctx) {
              this.ctx.putImageData(this.decoder.pending_image_data, 0, 0);
              this.decoder.pending_image_data = null;
            }
          });
        }
      : (imageData: ImageData) => {
          this.ctx?.putImageData(imageData, 0, 0);
        };

    this.decoder.set_image_callback((image) => {
      this.frames.decoded++;

      if (this.wantToPlay && this.state !== "seeking") {
        this.emitCanvasEvent("timeupdate");
      }

      // Create/resize ImageData if needed
      if (!this.decoder!.image_data) {
        const w = image.get_width();
        const h = image.get_height();
        if (this.canvas && this.ctx && (w !== this.canvas.width || h !== this.canvas.height)) {
          this.canvas.width = w;
          this.canvas.height = h;
          this.decoder!.image_data = this.ctx.createImageData(w, h);
        }
      }

      if (this.state !== "seeking") {
        image.display(this.decoder!.image_data!, (displayData) => {
          this.decoder!.decoding = false;
          displayImage(displayData);
        });
      }
      image.free();

      // State transitions after decode
      switch (this.state) {
        case "play":
        case "waiting":
          if (!this.dropping) {
            this.emitCanvasEvent("canplay");
            this.emitCanvasEvent("playing");
            this.state = "playing";
            if (!this.wantToPlay) {
              this.sendWs({ type: "hold" });
            }
          }
          break;
        case "seeking":
          if (this.seekTo !== null) {
            const t = this.frame2time(this.frames.decoded + this.frames.dropped);
            if (t >= this.seekTo) {
              this.emitCanvasEvent("seeked");
              this.seekTo = null;
              this.state = "playing";
              if (!this.wantToPlay) {
                this.emitCanvasEvent("timeupdate");
                this.sendWs({ type: "hold" });
              }
            }
          }
          break;
        default:
          this.state = "playing";
      }
    });
  }

  /**
   * Connect WebSocket to MistServer raw stream.
   * Ported from rawws.js:294-482
   */
  private connect(url: string): void {
    this.emitCanvasEvent("loadstart");

    // Append buffer=0 and select lowest bitrate HEVC track
    const wsUrl = this.appendUrlParams(url, { buffer: "0", video: "hevc,|minbps" });

    const ws = new WebSocket(wsUrl);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    ws.onopen = () => {
      if (this.isDestroyed) return;
      this.sendWs({ type: "request_codec_data", supported_codecs: ["HEVC"] });
    };

    ws.onclose = () => {
      if (this.isDestroyed) return;
      this.emit("error", "LibDe265: WebSocket closed");
    };

    ws.onerror = () => {
      if (this.isDestroyed) return;
      this.emit("error", "LibDe265: WebSocket error");
    };

    ws.onmessage = (e: MessageEvent) => {
      if (this.isDestroyed) return;

      if (typeof e.data === "string") {
        this.handleControlMessage(JSON.parse(e.data));
        return;
      }

      // Binary frame
      this.frames.received++;
      this.frames.bitsReceived += e.data.byteLength * 8;

      const chunk = parseRawChunk(e.data);
      this.frames.timestamps[this.frames.received] = chunk.timestamp;

      // Async processing to avoid blocking WS message queue (rawws.js:422)
      setTimeout(() => this.processFrame(chunk.data, chunk), 0);
    };
  }

  /**
   * Handle JSON control messages from MistServer.
   * Ported from rawws.js:336-404
   */
  private handleControlMessage(msg: any): void {
    switch (msg.type) {
      case "on_time":
        this.paused = false;
        if (msg.data?.end !== undefined) {
          const newDuration = msg.data.end * 1e-3;
          if (this.duration !== newDuration) {
            this.duration = newDuration;
            this.emitCanvasEvent("durationchange");
          }
        }
        break;

      case "seek":
        this.frames.timestamps = {};
        if (this.dropping && Array.isArray(this.dropping)) {
          this.frames.dropped += this.dropping.length;
          this.dropping = [];
        }
        break;

      case "codec_data":
        this.emitCanvasEvent("loadedmetadata");
        this.sendWs({ type: "play" });
        this.state = "play";
        break;

      case "pause":
        this.paused = msg.paused;
        if (msg.paused) {
          this.decoder?.flush();
          this.emitCanvasEvent("pause");
        }
        break;

      case "on_stop":
        if (this.state === "ended") return;
        this.state = "ended";
        this.paused = true;
        if (this.ws) {
          this.ws.onclose = () => {};
          this.ws.close();
        }
        this.decoder?.flush();
        this.emitCanvasEvent("ended");
        break;
    }
  }

  /**
   * Process a decoded frame with drop logic.
   * Ported from rawws.js:419-461, 484-522
   */
  private processFrame(data: Uint8Array, chunk: { type: string }): void {
    if (this.isDestroyed || !this.decoder) return;

    const isKey = chunk.type === "key";
    const behind = this.frames.received - this.frames.decoded - this.frames.dropped;

    if (this.dropping) {
      if (this.state !== "waiting") {
        this.emitCanvasEvent("waiting");
        this.state = "waiting";
      }

      if (isKey) {
        if (Array.isArray(this.dropping) && this.dropping.length) {
          this.frames.dropped += this.dropping.length;
          this.dropping = [];
        } else {
          this.dropping = false;
        }
      } else {
        if (Array.isArray(this.dropping)) {
          this.dropping.push([new Uint8Array(0), data]);
        }
        return;
      }
    } else if (behind > LibDe265PlayerImpl.DROP_THRESHOLD) {
      this.dropping = [];
    }

    // Push data to decoder (rawws.js:484-522)
    this.decoder.decoding = true;
    const err = this.decoder.push_data(data);

    if (this.state === "play") {
      this.emitCanvasEvent("loadeddata");
      this.state = "waiting";
    }

    if (this.wantToPlay && this.state !== "seeking") {
      this.emitCanvasEvent("progress");
    }

    const onerror = (decErr: number): boolean | void => {
      if (decErr === 0) return;
      if (decErr === libde265.DE265_ERROR_WAITING_FOR_INPUT_DATA) {
        this.state = "waiting";
        return;
      }
      if (!libde265.de265_isOK(decErr)) {
        this.emit("error", "Decode error: " + libde265.de265_get_error_text(decErr));
        return true;
      }
    };

    if (!onerror(err)) {
      this.decoder.decode(onerror);
    } else {
      this.decoder.free();
    }
  }

  // ========== PUBLIC API ==========

  async play(): Promise<void> {
    this.wantToPlay = true;
    if (this.ws && this.ws.readyState > WebSocket.OPEN) {
      this.emit("error", "LibDe265: WebSocket closed, cannot resume");
      return;
    }
    if (this.paused) {
      this.sendWs({ type: "play" });
    }
    this.state = "play";
  }

  pause(): void {
    this.wantToPlay = false;
    this.sendWs({ type: "hold" });
  }

  seek(time: number): void {
    this.emitCanvasEvent("seeking");
    this.state = "seeking";
    this.seekTo = time;
    this.sendWs({ type: "seek", seek_time: Math.round(time * 1000) });
  }

  getCurrentTime(): number {
    if (this.state === "seeking" && this.seekTo !== null) return this.seekTo;
    const n = this.frames.decoded + this.frames.dropped;
    return this.frame2time(n);
  }

  getDuration(): number {
    return this.duration;
  }

  isPaused(): boolean {
    return this.paused;
  }

  isLive(): boolean {
    return this.duration === 0 || !isFinite(this.duration);
  }

  async getStats(): Promise<any> {
    return {
      type: "libde265",
      framesReceived: this.frames.received,
      framesDecoded: this.frames.decoded,
      framesDropped: this.frames.dropped,
      framesBehind: this.frames.received - this.frames.decoded - this.frames.dropped,
      bitsReceived: this.frames.bitsReceived,
    };
  }

  async destroy(): Promise<void> {
    this.isDestroyed = true;

    if (this.ws) {
      this.ws.onclose = () => {};
      try {
        this.ws.close();
      } catch {}
      this.ws = null;
    }

    if (this.decoder) {
      try {
        this.decoder.push_data = () => 0;
        this.decoder.flush();
        this.decoder.free();
      } catch {}
      this.decoder = null;
    }

    if (this.canvas && this.container) {
      try {
        this.container.removeChild(this.canvas);
      } catch {}
    }

    if (this.proxyVideo && this.container) {
      try {
        this.container.removeChild(this.proxyVideo);
      } catch {}
    }

    this.canvas = null;
    this.ctx = null;
    this.proxyVideo = null;
    this.videoElement = null;
    this.container = null;
    this.listeners.clear();
  }

  // ========== UTILITIES ==========

  private frame2time(frameNumber: number): number {
    if (frameNumber in this.frames.timestamps) {
      return this.frames.timestamps[frameNumber] * 1e-3;
    }
    return 0;
  }

  private sendWs(cmd: object): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(cmd));
    }
  }

  private emitCanvasEvent(type: string): void {
    // Dispatch on the proxy video element for PlayerManager compatibility
    if (this.proxyVideo) {
      this.proxyVideo.dispatchEvent(new Event(type));
    }
  }

  private appendUrlParams(url: string, params: Record<string, string>): string {
    const separator = url.includes("?") ? "&" : "?";
    const queryString = Object.entries(params)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&");
    return url + separator + queryString;
  }
}
